import { BitmapFont, BitmapText, Container } from "pixi.js";
import type { Vector } from "@common/utils/vector";
import { CameraManager } from "./cameraManager";
import { PIXI_SCALE } from "../utils/constants";
import { GameConsole } from "../console/gameConsole";

/**
 * Floating damage numbers (setting: "Damage numbers").
 *
 * The server sends the damage this player dealt, merged per target per tick (a shotgun blast is one
 * number). Each number pops in with an overshoot, arcs up and sideways under gravity, then fades.
 * Bigger hits get warmer colours; the killing blow is red, larger and lingers a little longer.
 * Numbers live in world space (they stick to where the hit happened) but keep a constant on-screen
 * size whatever the scope zoom.
 */

interface Floater {
    readonly text: BitmapText
    readonly target?: number
    /** running total when rapid hits are stacked */
    total: number
    /** age at which the last pop started (stacking re-pops the number) */
    popStart: number
    /** spawn point in world px */
    readonly ox: number
    readonly oy: number
    /** offset from the spawn point in screen px */
    x: number
    y: number
    vx: number
    vy: number
    age: number
    life: number
    size: number
    kill: boolean
}

const STACK_WINDOW = 0.65; // seconds: a hit on the same target within this joins the running number
const SIZES: Readonly<Record<string, number>> = { small: 0.8, medium: 1, large: 1.3 };

const MAX_ACTIVE = 40;
// motion is defined in screen pixels so it reads the same at every scope zoom
const GRAVITY = 560; // screen px / s²

const colourFor = (amount: number, kill: boolean): number =>
    kill ? 0xff3b30
        : amount >= 60 ? 0xff8a1f
            : amount >= 30 ? 0xffd23f
                : 0xffffff;

/**
 * The digits, drawn once into a shared atlas (white, with their outline and shadow) and tinted per number: a new
 * number, or a stacked one counting up, is then just a few quads. Drawing each number as its own text means a canvas
 * render (with a blurred shadow) and a texture upload on every hit, in the middle of a fight.
 */
const FONT = "trench-damage-numbers";
let fontInstalled = false;
const installFont = (): void => {
    if (fontInstalled) return;
    fontInstalled = true;
    BitmapFont.install({
        name: FONT,
        chars: [["0", "9"]],
        resolution: 2,
        padding: 10,
        style: {
            fontFamily: "Inter",
            fontWeight: "700",
            fontStyle: "italic",
            fontSize: 34,
            fill: 0xffffff,
            stroke: { color: 0x15120c, width: 7, join: "round" },
            dropShadow: { color: 0x000000, alpha: 0.5, blur: 3, distance: 3, angle: Math.PI / 2 }
        }
    });
};

const easeOutBack = (t: number): number => {
    const c1 = 1.9; const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

class DamageNumberManager {
    private readonly _container = new Container({ zIndex: 1000 });
    private readonly _active: Floater[] = [];
    private _side = 1;

    private _attach(): void {
        if (this._container.parent !== CameraManager.container) {
            CameraManager.addObject(this._container);
        }
    }

    private _sizeFor(shown: number, kill: boolean): number {
        const scale = SIZES[String(GameConsole.getBuiltInCVar("cv_damage_numbers_size"))] ?? 1;
        return scale * (kill ? 1.45 : shown >= 60 ? 1.25 : shown >= 30 ? 1.1 : 1);
    }

    /** position in game units; `target` lets rapid hits on the same player stack into one number */
    spawn(position: Vector, amount: number, kill: boolean, target?: number): void {
        this._attach();
        if (target !== undefined && GameConsole.getBuiltInCVar("cv_damage_numbers_stack")) {
            const running = this._active.find(f => f.target === target && f.age - f.popStart < STACK_WINDOW && f.age < f.life * 0.6);
            if (running) {
                running.total += amount;
                running.kill ||= kill;
                const shown = Math.max(1, Math.round(running.total));
                running.text.text = String(shown);
                running.text.tint = colourFor(shown, running.kill);
                running.size = this._sizeFor(shown, running.kill);
                running.popStart = running.age;          // pop again
                running.life = running.age + (running.kill ? 1.35 : 0.95);
                running.vy = Math.min(running.vy, -120);  // a small hop instead of a new arc
                return;
            }
        }
        if (this._active.length >= MAX_ACTIVE) this._remove(0);

        const shown = Math.max(1, Math.round(amount));
        const size = this._sizeFor(shown, kill);
        installFont();
        const text = new BitmapText({ text: String(shown), style: { fontFamily: FONT, fontSize: 34 } });
        text.tint = colourFor(shown, kill);
        text.anchor.set(0.5);

        // alternate sides so a burst fans out left and right instead of stacking
        this._side = -this._side;
        const floater: Floater = {
            text,
            target,
            total: amount,
            popStart: 0,
            ox: position.x * PIXI_SCALE,
            oy: position.y * PIXI_SCALE,
            x: this._side * (4 + Math.random() * 10),
            y: -26,
            vx: this._side * (90 + Math.random() * 100),
            vy: -(270 + Math.random() * 60) - (kill ? 40 : 0),
            age: 0,
            life: kill ? 1.35 : 0.95,
            size,
            kill
        };
        this._container.addChild(text);
        this._active.push(floater);
        this._place(floater);
    }

    update(deltaMS: number): void {
        if (!this._active.length) return;
        const dt = Math.min(deltaMS, 50) / 1000;
        for (let i = this._active.length - 1; i >= 0; i--) {
            const f = this._active[i];
            f.age += dt;
            if (f.age >= f.life) { this._remove(i); continue; }
            f.vy += GRAVITY * dt;
            f.vx *= 1 - 1.6 * dt; // air drag on the sideways drift
            f.x += f.vx * dt;
            f.y += f.vy * dt;
            this._place(f);
        }
    }

    private _place(f: Floater): void {
        const zoom = CameraManager.container.scale.x || 1;
        const since = f.age - f.popStart;
        const pop = since < 0.24 ? (f.popStart > 0 ? 0.8 + 0.2 * easeOutBack(since / 0.24) : 0.35 + 0.65 * easeOutBack(since / 0.24)) : 1;
        // killing blows give a short shake while they pop
        const shake = f.kill && f.age < 0.3 ? Math.sin(f.age * 90) * 3 * (1 - f.age / 0.3) : 0;
        const fadeFrom = f.life * 0.62;
        f.text.alpha = f.age <= fadeFrom ? 1 : Math.max(0, 1 - (f.age - fadeFrom) / (f.life - fadeFrom));
        f.text.scale.set((f.size * pop) / zoom);
        f.text.position.set(f.ox + (f.x + shake) / zoom, f.oy + f.y / zoom);
    }

    private _remove(index: number): void {
        const [f] = this._active.splice(index, 1);
        f.text.destroy();
    }

    clear(): void {
        while (this._active.length) this._remove(this._active.length - 1);
    }
}

export const DamageNumbers = new DamageNumberManager();
